import { Widget, WidgetHeader, WidgetBody } from "../../../ui/Widget"
import { NumberInputRoot, NumberInputLabel, NumberInputField } from "../../../ui/number-input"
import { colors } from "../../../../utilities/colors"
import useStore from "../../../../store"
import { NeutralButton } from "../../../common/Buttons"
import { useState, useEffect } from "react"
import { MdAddAPhoto  } from "react-icons/md"
import { FaCheck, FaExclamationTriangle } from "react-icons/fa"
import { DateTime } from "luxon"
import { compressImage } from "../../../../utilities/imageUtilities"


export default function LotDetails({ fieldAudit, isVisible }) {
  const {
    fetchFieldAudit,
    updateFieldAudit,
    createDealerFile,
    setLotPhotoRequired,
    setSuccessMessage,
    setErrorMessage,
  } = useStore()

  const [updatingLotDetails, setUpdatingLotDetails] = useState(false)
  const [lotDetails, setLotDetails] = useState({})
  const [lotPhoto, setLotPhoto] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)

  const todaysDate = DateTime.now().toLocaleString(DateTime.DATE_SHORT)

  useEffect(() => {
    if (fieldAudit) {
      setLotDetails({
        current_inventory: fieldAudit.attributes['current-inventory']
      })
    }
  }, [fieldAudit])

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0]
    if (!file) {
      return
    }

    const compressedFile = await compressImage(file)
    setLotPhoto(compressedFile)

    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    reader.readAsDataURL(compressedFile)
  }

  const uploadLotPhoto = async () => {
    const formData = new FormData()
    formData.append('dealer_file[dfile]', lotPhoto)
    formData.append('dealer_file[dealer_folder_id]', fieldAudit.attributes.dealer.lot_photo_folder_id)
    formData.append('dealer_file[name]', `Lot Photo (${todaysDate})`)
    formData.append('dealer_file[description]', 'Created on portal application')

    const response = await createDealerFile(formData, fieldAudit.attributes.dealer.id)

    if (!response?.ok) {
      throw new Error(`Lot photo upload failed with status ${response?.status}`)
    }
  }

  const handleUpdateLotDetails = async () => {
    setUpdatingLotDetails(true)

    try {
      await Promise.all([
        updateFieldAudit(fieldAudit.id, lotDetails),
        ...(lotPhoto ? [uploadLotPhoto()] : [])
      ])

      if (lotPhoto) {
        setLotPhotoRequired(false)
      }

      setLotPhoto(null)
      setImagePreview(null)
      setSuccessMessage('Lot details updated successfully')
      fetchFieldAudit(fieldAudit.id)
    } catch (error) {
      console.error('Error updating lot details:', error)
      setErrorMessage('Error updating lot details')
    } finally {
      setUpdatingLotDetails(false)
    }
  }

  return (
    <Widget>
      <WidgetHeader
        title="Lot Details"
        showSpinner={updatingLotDetails}
      />
      <WidgetBody
        styles="flex flex-col items-center justify-center px-2 pb-2"
        isVisible={isVisible}
      >
        <NumberInputRoot
          value={lotDetails.current_inventory}
          onChange={(e) => setLotDetails({
            ...lotDetails,
            current_inventory: e.target.value
          })}
          skipIncrementer
          pb={2}
          className="w-full md:w-4/5"
        >
          <NumberInputLabel
            fontSize={'sm'}
            color={colors.grey300}
          >
            Estimate number of vehicles on the lot
          </NumberInputLabel>
          <NumberInputField
            bgColor={colors.grey900}
            borderRadius='md'
            _focus={{ borderColor: colors.grey100, ringColor: colors.grey100 }}
          />
        </NumberInputRoot>

        <div className="flex flex-col items-center justify-center mb-4 w-full md:w-4/5 gap-1">
          <div className="flex items-center justify-start w-full text-sm">
            {fieldAudit?.attributes?.dealer?.lot_photo_required ?
              <>
                <FaExclamationTriangle className="inline-block text-fpx-red mr-2" />
                Lot photo required
              </>
              :
              <>
                <FaCheck className="inline-block text-green mr-2" />
                Lot photo not required
              </>
            }
          </div>

          {imagePreview ?
            <div className='flex items-center justify-start gap-2'>
              <img
                src={imagePreview}
                alt="Lot Photo"
                className="h-20 object-cover rounded-md"
              />
              <button
                className="text-sm text-fpx-red"
                onClick={() => {
                  setLotPhoto()
                  setImagePreview(null)
                }}
              >
                Cancel
              </button>
            </div>
          :
            <>
              <input
                type="file"
                accept="image/*"
                capture="camera"
                id="photo-input"
                data-testid="photo-input"
                style={{ display: 'none' }}
                onChange={handlePhotoChange}
              />
              <label
                htmlFor="photo-input"
                className="flex justify-center items-center text-center bg-grey-700 rounded-md p-2 w-full"
              >
                <MdAddAPhoto className="text-3xl" />
              </label>
            </>
          }
        </div>


        <div className="w-4/5 md:w-1/2">
          <NeutralButton
            text="Update Lot Details"
            loading={updatingLotDetails}
            onClick={handleUpdateLotDetails}
          />
        </div>
      </WidgetBody>
    </Widget>
  )
}